import { db } from '../firebase';
import { collection, addDoc, getDocs, doc, getDoc, updateDoc, deleteDoc, query, where, serverTimestamp, runTransaction, getFirestore } from 'firebase/firestore';
import emailjs from '@emailjs/browser';

const SESSIONS_COLLECTION = 'sessions';
const PARTICIPANTS_COLLECTION = 'participants';

// Fonction pour initialiser les données (à utiliser une seule fois)
export async function initializeData() {
  try {
    // Vérifier si les données existent déjà
    const sessionsSnapshot = await getDocs(collection(db, SESSIONS_COLLECTION));
    
    if (sessionsSnapshot.empty) {
      console.log("Initialisation des données...");
      
      // Période du 14 juin au 31 août 2025
      const startDate = new Date('2025-06-14');
      const endDate = new Date('2025-08-31');
      
      // Horaires disponibles
      const availableHours = [
        '10:00', '10:30', '11:00', '11:30', 
        '14:00', '14:30', '15:00', '15:30', 
        '16:00', '16:30', '17:00'
      ];
      
      // Générer des sessions pour tous les jours
      const currentDate = new Date(startDate);
      const sessionPromises = [];
      
      while (currentDate <= endDate) {
        const dateStr = currentDate.toISOString().split('T')[0];
        
        for (const time of availableHours) {
          // 10% de chance d'être bloqué
          const isBlocked = Math.random() < 0.1;
          
          const sessionData = {
            date: dateStr,
            time,
            isBlocked,
            blockReason: isBlocked ? 'Réservé - Groupe' : '',
            participantCount: 0, // Toujours commencer à 0
            paidCount: 0,
            createdAt: serverTimestamp()
          };
          
          sessionPromises.push(addDoc(collection(db, SESSIONS_COLLECTION), sessionData));
        }
        
        // Passer au jour suivant
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      await Promise.all(sessionPromises);
      console.log("Données initialisées avec succès!");
    }
  } catch (error) {
    console.error("Erreur lors de l'initialisation des données:", error);
  }
}

// Récupérer toutes les sessions
export async function getSessions() {
  try {
    const sessionsCol = collection(db, SESSIONS_COLLECTION);
    const snapshot = await getDocs(sessionsCol);
    
    return snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convertir le timestamp Firestore en date si nécessaire
        createdAt: doc.data().createdAt ? doc.data().createdAt.toDate() : null
      }))
      // Filtrer les sessions supprimées pour l'affichage normal
      .filter(session => !session.isDeleted);
      
  } catch (error) {
    console.error("Erreur lors de la récupération des sessions:", error);
    throw error;
  }
}

// Récupérer tous les participants
export async function getParticipants() {
  try {
    const participantsCol = collection(db, PARTICIPANTS_COLLECTION);
    const snapshot = await getDocs(participantsCol);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Erreur lors de la récupération des participants:", error);
    throw error;
  }
}

// Récupérer les participants d'une session spécifique
export async function getSessionParticipants(sessionId) {
  try {
    const participantsCol = collection(db, PARTICIPANTS_COLLECTION);
    const q = query(participantsCol, where("sessionId", "==", sessionId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Erreur lors de la récupération des participants de la session:", error);
    throw error;
  }
}

// Recalculer les compteurs d'une session de manière plus stricte
export async function recalculateSessionCounters(sessionId) {
  try {
    // Récupérer tous les participants de cette session
    const participantsCol = collection(db, PARTICIPANTS_COLLECTION);
    const q = query(participantsCol, where("sessionId", "==", sessionId));
    const snapshot = await getDocs(q);
    
    const participants = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Calculer les compteurs réels en prenant en compte le nombre de joueurs
    let participantCount = 0;
    let paidCount = 0;
    
    participants.forEach(p => {
      const playerCount = p.playerCount || 1;
      participantCount += playerCount;
      if (p.paid) {
        paidCount += playerCount;
      }
    });
    
    console.log(`Recalcul pour session ${sessionId}: ${participantCount} joueurs total, ${paidCount} joueurs payés`);
    
    // Mettre à jour la session avec les valeurs exactes
    const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
    await updateDoc(sessionRef, {
      participantCount,
      paidCount
    });
    
    return { participantCount, paidCount };
  } catch (error) {
    console.error("Erreur lors du recalcul des compteurs:", error);
    throw error;
  }
}

// Ajouter un participant
export async function addParticipant(participantData) {
  try {
    // Log de début
    console.log('🔄 Début ajout participant pour session:', participantData.sessionId);

    // Récupérer le nombre de joueurs (par défaut 1)
    const playerCount = participantData.playerCount || 1;
    
    // Créer le participant
    const docRef = await addDoc(collection(db, PARTICIPANTS_COLLECTION), {
      ...participantData,
      playerCount, // Stocker le nombre de joueurs
      createdAt: serverTimestamp()
    });
    
    // Récupérer la session
    const sessionRef = doc(db, SESSIONS_COLLECTION, participantData.sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (sessionDoc.exists()) {
      const sessionData = sessionDoc.data();
      await updateDoc(sessionRef, {
        participantCount: (sessionData.participantCount || 0) + playerCount, // Ajouter le nombre de joueurs
        paidCount: participantData.paid ? (sessionData.paidCount || 0) + playerCount : (sessionData.paidCount || 0)
      });
    }
    
    // Récupérer le document complet pour le retourner
    const newDoc = await getDoc(docRef);

    // Log de fin
    console.log('✅ Participant ajouté, compteurs mis à jour');

    return {
      id: newDoc.id,
      ...newDoc.data()
    };
  } catch (error) {
    console.error("Erreur lors de l'ajout du participant:", error);
    throw error;
  }
}

// 🛡️ Fonction améliorée pour éviter les participants fantômes
export async function addParticipantSafe(participantData) {
  const db = getFirestore();

  try {
    // 1. TRANSACTION pour garantir la cohérence
    const result = await runTransaction(db, async (transaction) => {

      // Récupérer la session dans la transaction
      const sessionRef = doc(db, SESSIONS_COLLECTION, participantData.sessionId);
      const sessionDoc = await transaction.get(sessionRef);

      if (!sessionDoc.exists()) {
        throw new Error('Session introuvable');
      }

      const sessionData = sessionDoc.data();
      const playerCount = participantData.playerCount || 1;

      // Vérifier la capacité (optionnel)
      const currentTotal = sessionData.participantCount || 0;
      if (currentTotal + playerCount > 10) { // 10 = capacité max
        throw new Error('Session complète');
      }

      // Créer le participant
      const participantRef = doc(collection(db, PARTICIPANTS_COLLECTION));
      const participantDataWithId = {
        ...participantData,
        playerCount,
        createdAt: serverTimestamp()
      };

      // Mettre à jour la session
      const newParticipantCount = currentTotal + playerCount;
      const newPaidCount = participantData.paid 
        ? (sessionData.paidCount || 0) + playerCount 
        : (sessionData.paidCount || 0);

      // Écrire les deux en une seule transaction atomique
      transaction.set(participantRef, participantDataWithId);
      transaction.update(sessionRef, {
        participantCount: newParticipantCount,
        paidCount: newPaidCount
      });

      return {
        id: participantRef.id,
        ...participantDataWithId
      };
    });

    console.log('✅ Participant ajouté avec succès:', result.id);
    return result;

  } catch (error) {
    console.error('❌ Erreur lors de l\'ajout du participant:', error);
    throw error;
  }
}

// Mettre à jour un participant
export async function updateParticipant(participantId, data) {
  try {
    // Récupérer les données actuelles du participant
    const participantRef = doc(db, PARTICIPANTS_COLLECTION, participantId);
    const participantDoc = await getDoc(participantRef);
    
    if (!participantDoc.exists()) {
      throw new Error('Participant non trouvé');
    }
    
    const oldData = participantDoc.data();
    const playerCount = oldData.playerCount || 1;
    
    // Mettre à jour le participant
    await updateDoc(participantRef, data);
    
    // Mettre à jour les compteurs si le statut de paiement a changé
    if ('paid' in data && data.paid !== oldData.paid) {
      const sessionRef = doc(db, SESSIONS_COLLECTION, oldData.sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (sessionDoc.exists()) {
        const sessionData = sessionDoc.data();
        await updateDoc(sessionRef, {
          paidCount: data.paid 
            ? (sessionData.paidCount || 0) + playerCount 
            : Math.max(0, (sessionData.paidCount || 0) - playerCount)
        });
      }
    }
    
    return true;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du participant:", error);
    throw error;
  }
}

// Supprimer un participant
export async function deleteParticipant(participantId) {
  try {
    // Récupérer les données du participant
    const participantRef = doc(db, PARTICIPANTS_COLLECTION, participantId);
    const participantDoc = await getDoc(participantRef);
    
    if (!participantDoc.exists()) {
      throw new Error('Participant non trouvé');
    }
    
    const participantData = participantDoc.data();
    const sessionId = participantData.sessionId;
    const playerCount = participantData.playerCount || 1;
    const wasPaid = participantData.paid;
    
    // Supprimer le participant
    await deleteDoc(participantRef);
    
    // Mettre à jour les compteurs de la session
    const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (sessionDoc.exists()) {
      const sessionData = sessionDoc.data();
      await updateDoc(sessionRef, {
        participantCount: Math.max(0, (sessionData.participantCount || 0) - playerCount),
        paidCount: wasPaid ? Math.max(0, (sessionData.paidCount || 0) - playerCount) : (sessionData.paidCount || 0)
      });
    }
    
    return true;
  } catch (error) {
    console.error("Erreur lors de la suppression du participant:", error);
    throw error;
  }
}

// Bloquer une session
export async function blockSession(sessionId, reason, isGroupReservation = false, groupData = null) {
  try {
    if (isGroupReservation && groupData) {
      // Pour une réservation de groupe : on garde les données
      const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
      await updateDoc(sessionRef, {
        isBlocked: true,
        blockReason: reason,
        blockType: 'group', // Nouveau champ pour identifier le type
        groupReservationData: groupData, // Sauvegarder les données du groupe
        participantCount: groupData.playerCount || 1,
        paidCount: groupData.paid ? (groupData.playerCount || 1) : 0
      });
    } else {
      // Pour un blocage administratif : on supprime les participants existants
      const participants = await getSessionParticipants(sessionId);
      
      // Supprimer tous les participants
      const deletePromises = participants.map(participant => 
        deleteDoc(doc(db, PARTICIPANTS_COLLECTION, participant.id))
      );
      
      await Promise.all(deletePromises);
      
      // Mettre à jour la session
      const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
      await updateDoc(sessionRef, {
        isBlocked: true,
        blockReason: reason,
        blockType: 'admin',
        participantCount: 0,
        paidCount: 0
      });
    }
    
    return true;
  } catch (error) {
    console.error("Erreur lors du blocage de la session:", error);
    throw error;
  }
}

// Débloquer une session
export async function unblockSession(sessionId) {
  try {
    const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (!sessionDoc.exists()) {
      throw new Error('Session introuvable');
    }
    
    const sessionData = sessionDoc.data();
    
    if (sessionData.blockType === 'group' && sessionData.groupReservationData) {
      // Restaurer la réservation de groupe en tant que participant
      const groupData = sessionData.groupReservationData;
      
      // Créer un participant avec les données du groupe
      await addDoc(collection(db, PARTICIPANTS_COLLECTION), {
        name: groupData.name,
        phone: groupData.phone,
        email: groupData.email,
        playerCount: groupData.playerCount || 1,
        paid: groupData.paid || false,
        sessionId: sessionId,
        createdAt: serverTimestamp(),
        isGroupBooking: true // Marquer comme réservation de groupe
      });
      
      // Débloquer et mettre à jour les compteurs
      await updateDoc(sessionRef, {
        isBlocked: false,
        blockReason: '',
        blockType: null,
        groupReservationData: null,
        participantCount: groupData.playerCount || 1,
        paidCount: groupData.paid ? (groupData.playerCount || 1) : 0
      });
    } else {
      // Déblocage simple (blocage administratif)
      await updateDoc(sessionRef, {
        isBlocked: false,
        blockReason: '',
        blockType: null,
        participantCount: 0,
        paidCount: 0
      });
    }
    
    return true;
  } catch (error) {
    console.error("Erreur lors du déblocage de la session:", error);
    throw error;
  }
}

// Créer une nouvelle session
export async function createSession(sessionData) {
  try {
    // Ajouter le timestamp
    const dataWithTimestamp = {
      ...sessionData,
      createdAt: serverTimestamp()
    };
    
    // Créer la session
    const docRef = await addDoc(collection(db, SESSIONS_COLLECTION), dataWithTimestamp);
    
    // Récupérer le document complet pour le retourner
    const newDoc = await getDoc(docRef);
    return {
      id: newDoc.id,
      ...newDoc.data(),
      // Convertir le timestamp Firestore en date si nécessaire
      createdAt: newDoc.data().createdAt ? newDoc.data().createdAt.toDate() : null
    };
  } catch (error) {
    console.error("Erreur lors de la création de la session:", error);
    throw error;
  }
}

// Supprimer une session (marquer comme fermée)
export async function deleteSession(sessionId) {
  try {
    // Au lieu de supprimer complètement, marquer comme fermée
    const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
    await updateDoc(sessionRef, {
      isDeleted: true,           // Marquer comme supprimée
      deletedAt: serverTimestamp(),
      deletedBy: 'admin',
      participantCount: 0,
      paidCount: 0
    });
    
    console.log(`Session ${sessionId} marquée comme fermée`);
    return true;
  } catch (error) {
    console.error("Erreur lors de la fermeture de la session:", error);
    throw error;
  }
}

// Fonction pour recalculer tous les compteurs de sessions
export async function recalculateAllSessionCounters() {
  try {
    console.log("Début du recalcul de tous les compteurs...");
    
    // Récupérer toutes les sessions
    const sessionsCol = collection(db, SESSIONS_COLLECTION);
    const sessionsSnapshot = await getDocs(sessionsCol);
    
    // Récupérer tous les participants
    const participantsCol = collection(db, PARTICIPANTS_COLLECTION);
    const participantsSnapshot = await getDocs(participantsCol);
    
    const allParticipants = participantsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Traiter chaque session
    const updatePromises = [];
    let updatedCount = 0;
    
    for (const sessionDoc of sessionsSnapshot.docs) {
      const sessionId = sessionDoc.id;
      const sessionData = sessionDoc.data();
      
      // Trouver les participants de cette session
      const sessionParticipants = allParticipants.filter(p => p.sessionId === sessionId);
      
      // Calculer les vrais compteurs
      let participantCount = 0;
      let paidCount = 0;
      
      sessionParticipants.forEach(p => {
        const playerCount = p.playerCount && !isNaN(p.playerCount) ? parseInt(p.playerCount) : 1;
        participantCount += playerCount;
        if (p.paid) {
          paidCount += playerCount;
        }
      });
      
      // Mettre à jour seulement si les compteurs sont différents
      if (sessionData.participantCount !== participantCount || sessionData.paidCount !== paidCount) {
        console.log(`Session ${sessionId} (${sessionData.date} ${sessionData.time}): participants ${sessionData.participantCount}->${participantCount}, payés ${sessionData.paidCount}->${paidCount}`);
        const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
        updatePromises.push(
          updateDoc(sessionRef, {
            participantCount,
            paidCount
          })
        );
        updatedCount++;
      }
    }
    
    // Exécuter toutes les mises à jour
    await Promise.all(updatePromises);
    
    console.log(`Recalcul terminé: ${updatedCount} sessions mises à jour sur ${sessionsSnapshot.docs.length} sessions totales`);
    return {
      success: true,
      totalSessions: sessionsSnapshot.docs.length,
      updatedSessions: updatedCount
    };
    
  } catch (error) {
    console.error("Erreur lors du recalcul des compteurs:", error);
    throw error;
  }
}

// Fonction pour diagnostiquer les incohérences
export async function diagnoseDataInconsistencies() {
  try {
    const sessions = await getSessions();
    const participants = await getParticipants();
    
    const inconsistencies = [];
    
    sessions.forEach(session => {
      const sessionParticipants = participants.filter(p => p.sessionId === session.id);
      
      const realParticipantCount = sessionParticipants.reduce((sum, p) => {
        return sum + (p.playerCount && !isNaN(p.playerCount) ? parseInt(p.playerCount) : 1);
      }, 0);
      
      const realPaidCount = sessionParticipants.reduce((sum, p) => {
        if (p.paid) {
          return sum + (p.playerCount && !isNaN(p.playerCount) ? parseInt(p.playerCount) : 1);
        }
        return sum;
      }, 0);
      
      if (session.participantCount !== realParticipantCount || session.paidCount !== realPaidCount) {
        inconsistencies.push({
          sessionId: session.id,
          date: session.date,
          time: session.time,
          storedParticipants: session.participantCount,
          realParticipants: realParticipantCount,
          storedPaid: session.paidCount,
          realPaid: realPaidCount
        });
      }
    });
    
    return inconsistencies;
    
  } catch (error) {
    console.error("Erreur lors du diagnostic:", error);
    throw error;
  }
}

// 🔧 Fonction de nettoyage des participants orphelins
export async function cleanupOrphanedParticipants() {
  try {
    const participantsSnapshot = await getDocs(collection(db, PARTICIPANTS_COLLECTION));
    const sessionsSnapshot = await getDocs(collection(db, SESSIONS_COLLECTION));

    // Créer un Set des IDs de sessions valides
    const validSessionIds = new Set();
    sessionsSnapshot.forEach(doc => {
      validSessionIds.add(doc.id);
    });

    // Identifier les participants orphelins
    const orphanedParticipants = [];
    participantsSnapshot.forEach(doc => {
      const data = doc.data();
      if (!validSessionIds.has(data.sessionId)) {
        orphanedParticipants.push({
          id: doc.id,
          sessionId: data.sessionId,
          name: data.name
        });
      }
    });

    console.log(`🧹 ${orphanedParticipants.length} participants orphelins trouvés`);

    // Supprimer les participants orphelins (optionnel - à exécuter manuellement)
    if (orphanedParticipants.length > 0) {
      console.log('Participants orphelins:', orphanedParticipants);
      // Décommentez ces lignes pour supprimer automatiquement :
      // for (const participant of orphanedParticipants) {
      //   await deleteDoc(doc(db, PARTICIPANTS_COLLECTION, participant.id));
      // }
    }

    return orphanedParticipants;

  } catch (error) {
    console.error('Erreur lors du nettoyage:', error);
    throw error;
  }
}

// Fonction pour envoyer un email de confirmation
export async function sendConfirmationEmail(participantData, sessionData) {
  if (!participantData.email) {
    console.log('Pas d\'email fourni, aucun email de confirmation envoyé');
    return;
  }

  const EMAILJS_SERVICE_ID = 'service_yv02dnf';
  const EMAILJS_TEMPLATE_ID = 'template_a2rips3';
  const EMAILJS_PUBLIC_KEY = 'NDZ3UQiorhMdJfkEb';

  const templateParams = {
    to_email: participantData.email,
    to_name: participantData.name,
    session_date: sessionData.date,
    session_time: sessionData.time,
    player_count: participantData.playerCount || 1,
    participant_phone: participantData.phone || 'Non renseigné',
    total_price: `${(participantData.playerCount || 1) * 8}€`,
    booking_reference: `LSR-${Date.now().toString().slice(-6)}`
  };

  try {
    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams,
      EMAILJS_PUBLIC_KEY
    );
    console.log('Email de confirmation envoyé avec succès');
  } catch (error) {
    console.error('Erreur lors de l\'envoi de l\'email:', error);
  }
}

// Fonction pour rouvrir une session
export async function reopenSession(sessionId) {
  try {
    const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (!sessionDoc.exists()) {
      throw new Error('Session introuvable');
    }
    
    // Vérifier si la session est déjà débloquée
    const sessionData = sessionDoc.data();
    if (!sessionData.isBlocked) {
      console.log('La session est déjà ouverte');
      return true;
    }
    
    // Rouvrir la session
    await updateDoc(sessionRef, {
      isBlocked: false,
      blockReason: '',
      blockType: null
    });
    
    console.log(`Session ${sessionId} rouverte avec succès`);
    return true;
  } catch (error) {
    console.error("Erreur lors de la réouverture de la session:", error);
    throw error;
  }
}

